/**
 * Auth Storage Adapter
 * Unified interface for auth state with backward compatibility for legacy token keys.
 *
 * Contract:
 * - Default write path stores bearer token in sessionStorage (not localStorage).
 * - Read path remains backward-compatible with legacy localStorage keys (`token`, `userRoleToken`).
 * - Legacy localStorage token values are migrated into session storage on first read,
 *   then legacy keys are purged to reduce persistent exposure.
 */

// LLM:METADATA
// :topology: [Domain:Authentication | Role:Storage_Abstraction | Pattern:Adapter]
// :rationale: "Centralizes token storage logic with backward-compatible reads while hardening persistence defaults."
// :contract: pre: "None", post: "Auth token read/write follows adapter contract", invariant: "No default localStorage token writes"
// :references: AGENTS.md#L19
// LLM:END

import logger from "./logger"

const AUTH_STORAGE_GET_TOKEN = "[Auth Storage] GET_TOKEN"
const AUTH_STORAGE_SET_TOKEN = "[Auth Storage] SET_TOKEN"
const AUTH_STORAGE_CLEAR = "[Auth Storage] CLEAR"
const AUTH_STORAGE_GET_SNAPSHOT = "[Auth Storage] GET_SNAPSHOT"

const AUTH_KEYS = {
  TOKEN: "token",
  USER_ROLE_TOKEN: "userRoleToken",
  USER_ID: "userId",
  USER_ROLE: "userRole",
}

const SESSION_KEYS = {
  TOKEN: "authSessionToken",
  MARKETPLACE_OPERATIONS: "plastinka:marketplace:manual-operations:v1",
}

const TOKEN_ENCODING_PREFIX = "b64:"

function getLocalStorageSafe() {
  try {
    return typeof localStorage !== "undefined" ? localStorage : null
  } catch (error) {
    return null
  }
}

function getSessionStorageSafe() {
  try {
    return typeof sessionStorage !== "undefined" ? sessionStorage : null
  } catch (error) {
    return null
  }
}

function encodeTokenForStorage(token) {
  try {
    const utf8Safe = encodeURIComponent(token)
    return `${TOKEN_ENCODING_PREFIX}${btoa(utf8Safe)}`
  } catch (error) {
    logger.warn(AUTH_STORAGE_SET_TOKEN, "Falling back to raw token storage", {
      reason: error?.message,
    })
    return token
  }
}

function decodeStoredToken(rawToken) {
  if (!rawToken || typeof rawToken !== "string") {
    return null
  }

  if (!rawToken.startsWith(TOKEN_ENCODING_PREFIX)) {
    return rawToken
  }

  try {
    const encoded = rawToken.slice(TOKEN_ENCODING_PREFIX.length)
    return decodeURIComponent(atob(encoded))
  } catch (error) {
    logger.warn(AUTH_STORAGE_GET_TOKEN, "Failed to decode stored token", {
      reason: error?.message,
    })
    return null
  }
}

function purgeLegacyLocalTokenKeys(localStore) {
  if (!localStore) return
  localStore.removeItem(AUTH_KEYS.TOKEN)
  localStore.removeItem(AUTH_KEYS.USER_ROLE_TOKEN)
}

function migrateLegacyTokenIfNeeded(rawLegacyToken) {
  const sessionStore = getSessionStorageSafe()
  const localStore = getLocalStorageSafe()
  const token = decodeStoredToken(rawLegacyToken)

  if (!token) {
    purgeLegacyLocalTokenKeys(localStore)
    return null
  }

  const encodedToken = encodeTokenForStorage(token)
  if (sessionStore) {
    sessionStore.setItem(SESSION_KEYS.TOKEN, encodedToken)
  }

  purgeLegacyLocalTokenKeys(localStore)

  logger.info(
    AUTH_STORAGE_GET_TOKEN,
    "Migrated legacy localStorage token to session storage",
    {
      migrated: true,
    }
  )

  return token
}

/**
 * Get auth token with backward compatibility.
 * Read order:
 * 1) sessionStorage primary token key
 * 2) legacy localStorage token key (migrated + purged)
 * 3) legacy localStorage userRoleToken key (migrated + purged)
 *
 * @returns {string|null} token value or null
 */
export function getToken() {
  const sessionStore = getSessionStorageSafe()
  const localStore = getLocalStorageSafe()

  const sessionToken = decodeStoredToken(
    sessionStore?.getItem(SESSION_KEYS.TOKEN) || null
  )
  if (sessionToken) {
    logger.debug(
      AUTH_STORAGE_GET_TOKEN,
      "Token retrieved from session storage",
      {
        key: SESSION_KEYS.TOKEN,
        hasValue: true,
      }
    )
    return sessionToken
  }

  // Compatibility reads from legacy localStorage keys (required contract)
  let rawPrimaryLegacyToken = null
  try {
    rawPrimaryLegacyToken = localStorage.getItem(AUTH_KEYS.TOKEN)
  } catch (error) {
    rawPrimaryLegacyToken = localStore?.getItem(AUTH_KEYS.TOKEN) || null
  }
  if (rawPrimaryLegacyToken) {
    return migrateLegacyTokenIfNeeded(rawPrimaryLegacyToken)
  }

  let rawFallbackLegacyToken = null
  try {
    rawFallbackLegacyToken = localStorage.getItem(AUTH_KEYS.USER_ROLE_TOKEN)
  } catch (error) {
    rawFallbackLegacyToken =
      localStore?.getItem(AUTH_KEYS.USER_ROLE_TOKEN) || null
  }
  if (rawFallbackLegacyToken) {
    return migrateLegacyTokenIfNeeded(rawFallbackLegacyToken)
  }

  logger.debug(AUTH_STORAGE_GET_TOKEN, "No token found in storage", {
    checkedKeys: [
      SESSION_KEYS.TOKEN,
      AUTH_KEYS.TOKEN,
      AUTH_KEYS.USER_ROLE_TOKEN,
    ],
    hasValue: false,
  })

  return null
}

/**
 * Set auth token.
 * Hardened default writes to sessionStorage only.
 *
 * @param {string} token - token value to store
 */
export function setToken(token) {
  if (!token) {
    logger.warn(AUTH_STORAGE_SET_TOKEN, "Attempted to store empty token", {
      token: "[EMPTY]",
    })
    return
  }

  const sessionStore = getSessionStorageSafe()
  const localStore = getLocalStorageSafe()
  const encodedToken = encodeTokenForStorage(token)

  if (sessionStore) {
    sessionStore.setItem(SESSION_KEYS.TOKEN, encodedToken)
  }

  // Never persist bearer token in legacy localStorage keys on new writes.
  purgeLegacyLocalTokenKeys(localStore)

  logger.debug(AUTH_STORAGE_SET_TOKEN, "Token stored in session storage", {
    key: SESSION_KEYS.TOKEN,
    tokenLength: token.length,
    encoded: encodedToken !== token,
  })
}

/**
 * Clear all auth-related storage keys.
 */
export function clearAuthStorage() {
  const sessionStore = getSessionStorageSafe()
  const localStore = getLocalStorageSafe()

  sessionStore?.removeItem(SESSION_KEYS.TOKEN)
  sessionStore?.removeItem(SESSION_KEYS.MARKETPLACE_OPERATIONS)

  localStore?.removeItem(AUTH_KEYS.TOKEN)
  localStore?.removeItem(AUTH_KEYS.USER_ROLE_TOKEN)
  localStore?.removeItem(AUTH_KEYS.USER_ID)
  localStore?.removeItem(AUTH_KEYS.USER_ROLE)

  logger.debug(AUTH_STORAGE_CLEAR, "Auth storage cleared", {
    clearedKeys: [...Object.values(AUTH_KEYS), SESSION_KEYS.TOKEN],
  })
}

/**
 * Get current auth state snapshot for diagnostics.
 * @returns {{sessionToken:string|null, token:string|null, userRoleToken:string|null, userId:string|null, userRole:string|null, hasAnyToken:boolean}}
 */
export function getAuthSnapshot() {
  const sessionStore = getSessionStorageSafe()
  const localStore = getLocalStorageSafe()

  const snapshot = {
    sessionToken: sessionStore?.getItem(SESSION_KEYS.TOKEN) || null,
    token: localStore?.getItem(AUTH_KEYS.TOKEN) || null,
    userRoleToken: localStore?.getItem(AUTH_KEYS.USER_ROLE_TOKEN) || null,
    userId: localStore?.getItem(AUTH_KEYS.USER_ID) || null,
    userRole: localStore?.getItem(AUTH_KEYS.USER_ROLE) || null,
    hasAnyToken: !!(
      sessionStore?.getItem(SESSION_KEYS.TOKEN) ||
      localStore?.getItem(AUTH_KEYS.TOKEN) ||
      localStore?.getItem(AUTH_KEYS.USER_ROLE_TOKEN)
    ),
  }

  logger.debug(AUTH_STORAGE_GET_SNAPSHOT, "Auth snapshot retrieved", {
    hasAnyToken: snapshot.hasAnyToken,
    hasUserId: !!snapshot.userId,
    hasUserRole: !!snapshot.userRole,
  })

  return snapshot
}

export default {
  getToken,
  setToken,
  clearAuthStorage,
  getAuthSnapshot,
}
